import React, { useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import PropTypes from 'prop-types';
import * as Icon from 'react-feather';
import { useTranslation } from 'react-i18next';

import useNetworkStatus from '../../hooks/useNetworkStatus';

const VARIANTS = {
  offline: {
    icon: Icon.WifiOff,
    title: 'sipAlertOfflineTitle',
    message: 'sipAlertOfflineMessage'
  },
  unreachable: {
    icon: Icon.CloudOff,
    title: 'sipAlertUnreachableTitle',
    message: 'sipAlertUnreachableMessage'
  },
  unregistered: {
    icon: Icon.PhoneOff,
    title: 'sipAlertUnregisteredTitle',
    message: 'sipAlertUnregisteredMessage'
  },
  connecting: {
    icon: null,
    title: 'sipAlertConnectingTitle',
    message: 'sipAlertConnectingMessage'
  }
};

const STATUS_BADGE = {
  Register: 'text-bg-success',
  'Un-Register': 'text-bg-danger'
};

// Tells the agent why calls are not reaching them when the softphone loses its SIP
// registration, and offers a one-click re-register. Until now the only sign was the small
// "(Un-Register)" beside the name in the header, and missing calls read as a dialer fault.
//
// Deliberately not closable: it goes away only when the phone is registered again. An
// agent who dismissed it would just keep trying to dial. Logout is the one way out, for
// when the switch itself is down and there is nothing the agent can fix.
const SipConnectionModal = ({ isOpen, reason, sipStatus, isRetrying, onRetry, onLogout }) => {
  const { t } = useTranslation();
  const network = useNetworkStatus();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await onLogout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  // The browser's own offline flag wins: nothing else can recover until it is back, and
  // the softphone reconnects by itself on the `online` event.
  let variantKey = 'unregistered';
  if (!network.isOnline) variantKey = 'offline';
  else if (isRetrying) variantKey = 'connecting';
  else if (reason === 'unreachable') variantKey = 'unreachable';
  const variant = VARIANTS[variantKey];
  const VariantIcon = variant.icon;

  return (
    <Modal isOpen={isOpen} size="md" centered backdrop="static" keyboard={false}>
      <ModalBody className="px-4">
        <div className="d-flex flex-column align-items-center pt-4 text-center">
          {VariantIcon ? (
            <VariantIcon size={60} className="text-danger" />
          ) : (
            <Spinner color="secondary" style={{ width: 60, height: 60 }} />
          )}
          <div className="fs-4 pt-3 fw-semibold text-dark">{t(variant.title)}</div>
          <div className="fs-6 pt-2 text-muted">{t(variant.message)}</div>
          <span
            className={`badge rounded-pill mt-3 px-3 py-2 ${STATUS_BADGE[sipStatus] || 'text-bg-secondary'}`}>
            {t('sipAlertStatusLabel')}: {sipStatus}
          </span>
        </div>

        {!isRetrying && (
          <div className="border rounded-3 p-3 mt-4">
            <div className="fw-semibold text-dark pb-2">{t('sipAlertStepsTitle')}</div>
            <ul className="mb-0 ps-3 small text-muted">
              <li>{t('sipAlertStepNetwork')}</li>
              <li>{t('sipAlertStepReconnect')}</li>
              <li>{t('sipAlertStepSupport')}</li>
            </ul>
          </div>
        )}

        <div className="small text-muted text-center pt-3">{t('sipAlertAutoRetryNote')}</div>
      </ModalBody>
      <ModalFooter className="justify-content-center">
        {/* Outlined, so the filled Reconnect stays the obvious next step. Disabled offline
            like Reconnect: the logout API call cannot reach the server either. */}
        <Button
          onClick={handleLogout}
          disabled={isLoggingOut || !network.isOnline}
          className="d-flex justify-content-center align-items-center gap-2 px-4 bg-transparent text-danger border-danger">
          {isLoggingOut ? <Spinner size="sm" color="danger" /> : <Icon.Power size={16} />}
          {t('logout')}
        </Button>
        <Button
          onClick={onRetry}
          disabled={isRetrying || isLoggingOut || !network.isOnline}
          className="d-flex justify-content-center align-items-center gap-2 px-4 bg-primary border-primary">
          {isRetrying ? <Spinner size="sm" color="light" /> : <Icon.RefreshCw size={16} />}
          {isRetrying ? t('sipAlertReconnecting') : t('sipAlertReconnect')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

SipConnectionModal.propTypes = {
  isOpen: PropTypes.bool,
  reason: PropTypes.oneOf(['offline', 'unreachable', 'unregistered']),
  sipStatus: PropTypes.string,
  isRetrying: PropTypes.bool,
  onRetry: PropTypes.func,
  onLogout: PropTypes.func
};

export default SipConnectionModal;
